import React, { useState, useEffect } from "react";
import { Form, Dropdown, Label, Grid, Checkbox, Icon } from "semantic-ui-react";
import Joi from "joi";
import { isEmpty, map, includes, trim, isEqual, get } from "lodash";
import PropTypes from "prop-types";
// Components
import Input from "@Components/Input";
import Text from "@Components/Text";
import IKModal from "@Components/IkModal";
import ColorPickerDropdown from "@Features/Tags/Component/DropDownColor";
import CategoryCrud from "@Features/Category/Modals/CategoryCrud";
// Modals
import ConfirmModal from "@Modals/ConfirmModal";
// Utils
import { dropdownFormat } from "@Utils/formats";
// redux
import genericAction from "@Actions/generic";
import { connect } from "react-redux";
// Styles
import styles from "./styles.module.css";

const TagsCrud = ({
	tableValue,
	modalCrud,
	onClose,
	categoryList,
	groupsList,
	apiData,
	dispatch,
	hideAddCategory,
	forcePinned,
	disableCategory,
}) => {
	const schema = Joi.object().keys({
		nametyping: Joi.string().min(4).messages({
			"string.min": "Nombre debe tener mínimo 4 caracteres",
			"string.empty": "Nombre es un campo requerido",
		}),
	});

	const [open, setOpen] = useState(false);
	const [categoryData, setCategoryData] = useState([]);
	const [departamentData, setDepartamentData] = useState([]);
	const [errors, setErrors] = useState([]);
	const [isPinned, setIsPinned] = useState(forcePinned || false);
	const [openCategoryCrud, setOpenCategoryCrud] = useState(false);
	const [typesList, setTypesList] = useState([]);
	const [initialFormData, setInitialFormData] = useState([]);
	const [modalOption, setModalOption] = useState();
	const [formData, setFormData] = useState({
		nametyping: "",
		category: "",
		departament: "",
		color: "#424949",
		isActive: true,
	});

	useEffect(() => {
		const fetchData = async () => {
			const filteredCategories = categoryList.filter((category) => {
				return includes(category.restriction, "tags");
			});
			const categoryDataVal = dropdownFormat(filteredCategories, "name", "id");

			const departamentDataVal = groupsList.map((item) => {
				return {
					key: item.id,
					value: item.id,
					text: item.name,
				};
			});

			setCategoryData(categoryDataVal);
			setDepartamentData(departamentDataVal);
			setModalOption(modalCrud);

			if (!isEmpty(tableValue)) {
				setFormData({
					nametyping: tableValue.name,
					category: get(tableValue, "category.id", ""),
					departament: map(get(tableValue, "groups", []), "id"),
					color: get(tableValue, "color", "#424949"),
					isActive: get(tableValue, "isActive", true),
				});

				setInitialFormData({
					nametyping: tableValue.name,
					category: get(tableValue, "category.id", ""),
					departament: map(get(tableValue, "groups", []), "id"),
					color: get(tableValue, "color", "#424949"),
					isActive: get(tableValue, "isActive", true),
				});
			} else {
				setFormData({
					nametyping: "",
					category: "",
					departament: "",
					color: "#424949",
					isActive: true,
				});

				setInitialFormData({
					nametyping: "",
					category: "",
					departament: "",
					color: "#424949",
					isActive: true,
				});
			}
		};

		fetchData();
	}, [tableValue]);

	// save handlers
	const handlerSaved = async () => {
		const validation = schema.validate(
			{
				nametyping: formData.nametyping,
			},
			{ abortEarly: false }
		);

		if (validation.error) {
			const { details } = validation.error;
			let errorsVal = {
				nametyping: "",
			};
			map(details, (item) => {
				errorsVal = { ...errorsVal, [item.context.key]: item.message };
			});
			setErrors(errorsVal);
		} else {
			const groupIds = formData.departament || [];
			const initialGroups = initialFormData.departament || [];

			const groupIdsAdd = groupIds.filter((id) => {
				return !initialGroups.includes(id);
			});
			const groupIdsRemove = initialGroups.filter((id) => {
				return !groupIds.includes(id);
			});

			const data = {
				name: trim(formData.nametyping),
				categoryId: formData.category ? parseInt(formData.category, 10) : null,
				color: formData.color,
				isActive: formData.isActive,
				groupIds: modalOption === "Add" ? groupIds : groupIdsAdd,
				groupIdsD: modalOption === "Edit" ? groupIdsRemove : undefined,
			};

			if (modalOption === "Add") {
				const dataAdd = await dispatch(genericAction({ method: "post", route: "tags", body: data }));
				if (!isEmpty(dataAdd)) {
					apiData("Add", data);
					if (isPinned) {
						setFormData({
							nametyping: "",
							category: formData.category,
							departament: "",
							color: "",
							isActive: true,
						});
					} else {
						onClose();
					}
				}
			} else {
				const dataEdit = await dispatch(genericAction({ method: "patch", route: `tags/${tableValue.id}`, body: data }));
				if (!isEmpty(dataEdit)) {
					apiData("Edit");
				}
			}
		}
	};

	const colorHandler = (name, value) => {
		setFormData({
			...formData,
			color: value,
		});
	};

	const handleCreateCategory = async () => {
		const typesCategoryPromise = await dispatch(genericAction({ method: "get", route: "types/categories" }));
		setTypesList(typesCategoryPromise);
		setOpenCategoryCrud(true);
	};

	const apiDataCategory = async (action, newCategory) => {
		if (action === "Add" && newCategory) {
			const updatedCategories = await dispatch(genericAction({ method: "get", route: "category/all" }));
			const filteredCategories = updatedCategories.filter((category) => {
				return includes(category.restriction, "tags");
			});
			const categoryDataVal = dropdownFormat(filteredCategories, "name", "id");
			setCategoryData(categoryDataVal);
			setFormData((prev) => {
				return { ...prev, category: newCategory.id };
			});
		}
	};

	// input handlers
	const handleInputDropdown = (event, { name, value }) => {
		setFormData({
			...formData,
			[name]: value,
		});
	};

	const handleInput = (e) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});
	};
	// modal handlers
	const cancelModal = () => {
		onClose();
	};

	const modalOpen = () => {
		if (isEqual(initialFormData, formData)) {
			cancelModal();
		} else if (open) {
			setOpen(false);
		} else {
			setOpen(true);
		}
	};

	return (
		<>
			<IKModal
				title={modalOption === "Add" ? "Agregar Etiqueta" : `Etiqueta: ${get(tableValue, "name", "")} `}
				id={tableValue.id}
				mode={modalOption}
				setMode={setModalOption}
				onClose={modalOpen}
				onSuccess={handlerSaved}
				headerButtons={[
					<Checkbox
						key="isActive"
						toggle
						label={formData.isActive ? "Activa" : "Inactiva"}
						name="isActive"
						disabled={modalOption === "Watch"}
						checked={formData.isActive}
						onChange={(e, { checked }) => {
							setFormData((prev) => {
								return { ...prev, isActive: checked };
							});
						}}
						className={styles.headerCheckbox}
					/>,
				]}
				fullHeight
				size="tiny">
				<Grid className={styles.grid}>
					<Grid.Row className={styles.grid}>
						<Grid.Column width={16} className={styles.grid}>
							<fieldset className={styles.fieldset}>
								<div>
									<Form>
										<div>
											<Input
												value={formData.nametyping}
												required
												disabled={modalOption === "Watch"}
												label="Nombre:"
												placeholder="Nombre"
												name="nametyping"
												fluid
												error={!isEmpty(errors.nametyping) && { content: errors.nametyping, pointing: "above" }}
												onChange={handleInput}
												className={styles.input}
											/>
											<Text className={styles.textBox}>Categoría:</Text>
											<div className={styles.categoryRow}>
												<div className={styles.categoryDropdown}>
													<Dropdown
														fluid
														placeholder="Selecciona una Categoría..."
														value={formData.category}
														size="mini"
														disabled={modalOption === "Watch" || disableCategory}
														name="category"
														clearable
														search
														selection
														onChange={handleInputDropdown}
														options={categoryData}
														className={styles.dropdownOverride}
													/>
												</div>
												{modalOption === "Add" && (
													<div className={styles.iconsWrapper}>
														<div
															className={`${styles.pinIcon} ${isPinned ? styles.pinIconActive : ""}`}
															role="button"
															tabIndex={0}
															onClick={() => {
																return setIsPinned(!isPinned);
															}}
															onKeyDown={(e) => {
																if (e.key === "Enter" || e.key === " ") {
																	e.preventDefault();
																	setIsPinned(!isPinned);
																}
															}}
															title="Fijar categoría">
															<Icon name="pin" size="large" style={{ margin: 0 }} />
														</div>
														{!hideAddCategory && (
															<div
																className={`${styles.pinIcon} ${styles.plusIcon}`}
																role="button"
																tabIndex={0}
																onClick={handleCreateCategory}
																onKeyDown={(e) => {
																	if (e.key === "Enter" || e.key === " ") {
																		e.preventDefault();
																		handleCreateCategory();
																	}
																}}
																title="Crear categoría">
																<Icon name="plus" size="large" style={{ margin: 0 }} />
															</div>
														)}
													</div>
												)}
											</div>
											<Text className={isEmpty(errors.departament) ? styles.textBox : styles.textBoxColor}>Grupos:</Text>
											<Dropdown
												fluid
												value={formData.departament}
												options={departamentData}
												placeholder="Selecciona un Grupo..."
												name="departament"
												clearable
												disabled={modalOption === "Watch"}
												error={!isEmpty(errors.departament)}
												onChange={handleInputDropdown}
												className={`${styles.dropdownOverride} ${styles.gruposDropdown}`}
												multiple
												selection
											/>
											<Label
												className={styles.messageError}
												style={isEmpty(errors.departament) ? { display: "none" } : null}
												pointing
												prompt>
												{errors.departament}
											</Label>
											<div className={styles.buttonPicker}>
												<ColorPickerDropdown
													currentColor={formData.color}
													setCurrentColor={(value) => {
														return colorHandler("color", value);
													}}
													disabled={modalOption === "Watch"}
												/>
											</div>
										</div>
									</Form>
								</div>
							</fieldset>
						</Grid.Column>
					</Grid.Row>
				</Grid>
			</IKModal>
			{open && (
				<ConfirmModal
					visible
					centerModal
					header="Salir del formulario"
					content="Está seguro que desea salir del formulario"
					onConfirm={cancelModal}
					onDismiss={modalOpen}
				/>
			)}

			{openCategoryCrud && (
				<CategoryCrud
					typesList={typesList}
					apiData={apiDataCategory}
					tableValue={[]}
					onClose={() => {
						return setOpenCategoryCrud(false);
					}}
					modalCrud="Add"
					defaultType="tags"
					refresh={() => {}}
				/>
			)}
		</>
	);
};

function mapStateToProps({ user, company }) {
	return {
		companies: [],
		companyName: company.name,
		userName: user.name,
	};
}

TagsCrud.propTypes = {
	tableValue: PropTypes.array,
	onClose: PropTypes.func,
	categoryList: PropTypes.array,
	groupsList: PropTypes.array,
	apiData: PropTypes.func,
	dispatch: PropTypes.func,
	modalCrud: PropTypes.string,
	hideAddCategory: PropTypes.bool,
	forcePinned: PropTypes.bool,
	disableCategory: PropTypes.bool,
};
export default connect(mapStateToProps)(TagsCrud);
